import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../styles/design-system.js';
import { iconStyles } from '../styles/icon-styles.js';

export class KnowledgeBase extends LitElement {
  static properties = {
    documents: { type: Array },
    selectedFolder: { type: String },
    viewMode: { type: String }
  };

  static styles = [
    designSystem,
    componentStyles,
    css`
      :host {
        display: block;
      }

      /* ─── Page Header ─── */
      .page-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        margin-bottom: var(--space-8);
      }

      .page-header-text {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }

      .page-title {
        font-family: var(--font-display);
        font-size: var(--text-4xl);
        font-weight: var(--font-black);
        color: var(--color-text-primary);
        letter-spacing: -0.04em;
      }

      .page-description {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }

      .header-actions {
        display: flex;
        gap: var(--space-3);
      }

      /* ─── Stats Row ─── */
      .stats-row {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: var(--space-4);
        margin-bottom: var(--space-8);
      }

      .stat-card {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        display: flex;
        align-items: center;
        gap: var(--space-4);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      .stat-icon {
        width: 44px;
        height: 44px;
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.125rem;
        flex-shrink: 0;
      }

      .stat-icon.documents {
        background: var(--color-primary-light);
        color: var(--color-primary);
      }

      .stat-icon.size {
        background: var(--color-accent-light);
        color: var(--color-accent);
      }

      .stat-icon.agents {
        background: var(--color-info-light);
        color: var(--color-info);
      }

      .stat-content {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      /* ─── Two-Column Layout ─── */
      .content-layout {
        display: grid;
        grid-template-columns: 240px 1fr;
        gap: var(--space-6);
        min-height: 600px;
      }

      /* ─── Sidebar ─── */
      .sidebar {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-lg);
        padding: var(--space-4);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        height: fit-content;
        position: sticky;
        top: var(--space-4);
        overflow: hidden;
      }

      .sidebar-title {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
        color: var(--color-text-tertiary);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
        padding: var(--space-2) var(--space-3);
        margin-bottom: var(--space-1);
      }

      .folder-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-px);
      }

      .folder-item {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-2) var(--space-3);
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-base);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
        user-select: none;
      }

      .folder-item:hover {
        background: var(--color-bg-tertiary);
        color: var(--color-text-primary);
      }

      .folder-item.active {
        background: var(--color-primary-light);
        color: var(--color-primary);
        font-weight: var(--font-medium);
      }

      .folder-item i {
        width: 18px;
        text-align: center;
        font-size: var(--text-sm);
        flex-shrink: 0;
      }

      .folder-name {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .folder-count {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        background: var(--color-bg-tertiary);
        padding: 1px 6px;
        border-radius: var(--radius-full);
        min-width: 22px;
        text-align: center;
      }

      .folder-item.active .folder-count {
        background: var(--color-primary-subtle);
        color: var(--color-primary);
      }

      /* ─── Main Content ─── */
      .main-content {
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
      }

      /* ─── Upload Drop Zone ─── */
      .drop-zone {
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-8) var(--space-6);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--space-3);
        cursor: pointer;
        transition: all var(--transition-base);
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      .drop-zone:hover {
        border-color: var(--color-primary);
        background: var(--color-primary-light);
      }

      .drop-zone-icon {
        font-size: 2rem;
        color: var(--color-text-tertiary);
        transition: color var(--transition-base);
      }

      .drop-zone:hover .drop-zone-icon {
        color: var(--color-primary);
      }

      .drop-zone-text {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
        text-align: center;
      }

      .drop-zone-text strong {
        color: var(--color-primary);
      }

      .drop-zone-hint {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-disabled);
      }

      /* ─── Content Toolbar ─── */
      .content-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .content-toolbar-left {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .content-toolbar-right {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .results-count {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }

      .view-toggle-btn {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid var(--color-border-primary);
        background: var(--color-bg-secondary);
        border-radius: var(--radius-md);
        color: var(--color-text-tertiary);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-size: var(--text-sm);
      }

      .view-toggle-btn:hover {
        color: var(--color-text-primary);
        border-color: var(--color-border-secondary);
      }

      .view-toggle-btn.active {
        background: var(--color-accent-light);
        color: var(--color-accent);
        border-color: var(--color-accent-subtle);
      }

      /* ─── Document Grid ─── */
      .doc-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        gap: var(--space-4);
      }

      .doc-card {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-4);
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
        transition: all var(--transition-base);
        cursor: pointer;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      .doc-card:hover {
        box-shadow: var(--shadow-elevated);
        border-color: var(--color-border-secondary);
        transform: scale(1.005);
      }

      .doc-card-header {
        display: flex;
        align-items: flex-start;
        gap: var(--space-3);
      }

      .doc-type-icon {
        width: 40px;
        height: 40px;
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.125rem;
        flex-shrink: 0;
      }

      .doc-type-icon.pdf {
        background: #FEF2F2;
        color: #DC2626;
      }

      .doc-type-icon.docx {
        background: #EFF6FF;
        color: #2563EB;
      }

      .doc-type-icon.txt {
        background: var(--color-bg-tertiary);
        color: var(--color-text-tertiary);
      }

      .doc-type-icon.url {
        background: #F0FDF4;
        color: #16A34A;
      }

      .doc-info {
        flex: 1;
        min-width: 0;
      }

      .doc-name {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        color: var(--color-text-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-bottom: 2px;
      }

      .doc-meta {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .doc-meta-sep {
        width: 3px;
        height: 3px;
        border-radius: var(--radius-full);
        background: var(--color-text-disabled);
      }

      .doc-card-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-top: var(--space-3);
        border-top: 1px solid var(--color-border-subtle);
      }

      .doc-footer-left {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .doc-agents-count {
        display: flex;
        align-items: center;
        gap: var(--space-1);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .doc-agents-count i {
        font-size: var(--text-xs);
      }

      .doc-actions {
        display: flex;
        gap: var(--space-1);
      }

      .doc-action-btn {
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        background: transparent;
        color: var(--color-text-tertiary);
        border-radius: var(--radius-sm);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-size: var(--text-xs);
      }

      .doc-action-btn:hover {
        background: var(--color-bg-tertiary);
        color: var(--color-text-primary);
      }

      .doc-action-btn.danger:hover {
        background: var(--color-error-light);
        color: var(--color-error);
      }

      /* ─── Document List View ─── */
      .doc-list {
        display: flex;
        flex-direction: column;
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-lg);
        overflow: hidden;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
      }

      .doc-list-item {
        display: grid;
        grid-template-columns: 40px 1fr 80px 100px 120px 80px 100px;
        gap: var(--space-3);
        align-items: center;
        padding: var(--space-3) var(--space-4);
        border-bottom: 1px solid var(--color-border-subtle);
        transition: background var(--transition-fast);
        cursor: pointer;
      }

      .doc-list-item:last-child {
        border-bottom: none;
      }

      .doc-list-item:hover {
        background: var(--color-bg-hover);
      }

      .doc-list-name {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .doc-list-size {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        text-align: right;
      }

      .doc-list-date {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .doc-list-agents {
        display: flex;
        align-items: center;
        gap: var(--space-1);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .doc-list-actions {
        display: flex;
        gap: var(--space-1);
        justify-content: flex-end;
      }

      /* ─── Processing badge spin ─── */
      .badge-warning i.fa-spinner-third {
        animation: spin 1s linear infinite;
      }

      /* ─── Responsive ─── */
      @media (max-width: 1024px) {
        .stats-row {
          grid-template-columns: 1fr;
        }

        .content-layout {
          grid-template-columns: 1fr;
        }

        .sidebar {
          position: static;
        }

        .folder-list {
          flex-direction: row;
          flex-wrap: wrap;
          gap: var(--space-2);
        }

        .folder-item {
          padding: var(--space-2) var(--space-3);
        }
      }

      @media (max-width: 768px) {
        .page-header {
          flex-direction: column;
          gap: var(--space-3);
        }

        .header-actions {
          width: 100%;
        }

        .header-actions .btn {
          flex: 1;
        }

        .doc-grid {
          grid-template-columns: 1fr;
        }

        .doc-list-item {
          grid-template-columns: 40px 1fr 80px 100px;
        }

        .doc-list-size,
        .doc-list-date,
        .doc-list-actions {
          display: none;
        }
      }
    `
  ];

  constructor() {
    super();
    this.selectedFolder = 'all';
    this.viewMode = 'grid';
    this.documents = this._generateMockDocuments();
  }

  _generateMockDocuments() {
    return [
      {
        id: 1,
        name: 'Reservation Policy Guide',
        type: 'pdf',
        size: '2.4 MB',
        sizeBytes: 2516582,
        uploadDate: '2026-01-15',
        folder: 'policies',
        status: 'ready',
        linkedAgents: 2,
        description: 'Complete guide for reservation policies and procedures'
      },
      {
        id: 2,
        name: 'Cancellation & Refund FAQ',
        type: 'docx',
        size: '856 KB',
        sizeBytes: 876544,
        uploadDate: '2026-01-22',
        folder: 'faqs',
        status: 'ready',
        linkedAgents: 2,
        description: 'Frequently asked questions about cancellations and refunds'
      },
      {
        id: 3,
        name: 'Check-in Procedures',
        type: 'pdf',
        size: '1.8 MB',
        sizeBytes: 1887436,
        uploadDate: '2026-02-01',
        folder: 'policies',
        status: 'ready',
        linkedAgents: 1,
        description: 'Step-by-step check-in and check-out procedures'
      },
      {
        id: 4,
        name: 'Amenities & Services List',
        type: 'txt',
        size: '124 KB',
        sizeBytes: 126976,
        uploadDate: '2026-02-03',
        folder: 'faqs',
        status: 'ready',
        linkedAgents: 2,
        description: 'Complete list of property amenities and guest services'
      },
      {
        id: 5,
        name: 'https://dewberry.com/rates',
        type: 'url',
        size: '48 KB',
        sizeBytes: 49152,
        uploadDate: '2026-02-05',
        folder: 'training',
        status: 'ready',
        linkedAgents: 1,
        description: 'Live rates page - auto-synced daily'
      },
      {
        id: 6,
        name: 'VIP Guest Handling Protocol',
        type: 'pdf',
        size: '3.1 MB',
        sizeBytes: 3250585,
        uploadDate: '2026-02-06',
        folder: 'training',
        status: 'processing',
        linkedAgents: 0,
        description: 'Training document for VIP guest interactions'
      },
      {
        id: 7,
        name: 'Seasonal Promotions Q2',
        type: 'docx',
        size: '1.2 MB',
        sizeBytes: 1258291,
        uploadDate: '2026-02-08',
        folder: 'training',
        status: 'error',
        linkedAgents: 0,
        description: 'Q2 seasonal promotion details and scripts'
      },
      {
        id: 8,
        name: 'https://dewberry.com/faq',
        type: 'url',
        size: '92 KB',
        sizeBytes: 94208,
        uploadDate: '2026-02-09',
        folder: 'faqs',
        status: 'processing',
        linkedAgents: 0,
        description: 'Website FAQ page - crawled content'
      }
    ];
  }

  _getFolders() {
    return [
      { id: 'all', name: 'All Documents', icon: 'fa-sharp fa-light fa-folder-open', count: this.documents.length },
      { id: 'faqs', name: 'FAQs', icon: 'fa-sharp fa-light fa-circle-question', count: this.documents.filter(d => d.folder === 'faqs').length },
      { id: 'policies', name: 'Policies', icon: 'fa-sharp fa-light fa-file-shield', count: this.documents.filter(d => d.folder === 'policies').length },
      { id: 'training', name: 'Training Data', icon: 'fa-sharp fa-light fa-graduation-cap', count: this.documents.filter(d => d.folder === 'training').length }
    ];
  }

  _getFilteredDocuments() {
    if (this.selectedFolder === 'all') {
      return this.documents;
    }
    return this.documents.filter(d => d.folder === this.selectedFolder);
  }

  _getDocTypeIcon(type) {
    switch (type) {
      case 'pdf': return 'fa-sharp fa-light fa-file-pdf';
      case 'docx': return 'fa-sharp fa-light fa-file-word';
      case 'txt': return 'fa-sharp fa-light fa-file-lines';
      case 'url': return 'fa-sharp fa-light fa-globe';
      default: return 'fa-sharp fa-light fa-file';
    }
  }

  _getTotalSize() {
    const totalBytes = this.documents.reduce((sum, d) => sum + d.sizeBytes, 0);
    if (totalBytes >= 1048576) {
      return (totalBytes / 1048576).toFixed(1) + ' MB';
    }
    return (totalBytes / 1024).toFixed(0) + ' KB';
  }

  _getLinkedAgentsTotal() {
    const unique = new Set();
    this.documents.forEach(d => {
      if (d.linkedAgents > 0) unique.add(d.id);
    });
    return Math.max(...this.documents.map(d => d.linkedAgents), 0);
  }

  _selectFolder(folderId) {
    this.selectedFolder = folderId;
  }

  _setViewMode(mode) {
    this.viewMode = mode;
  }

  _formatDate(dateStr) {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  _renderStatusBadge(status) {
    switch (status) {
      case 'ready':
        return html`<span class="badge badge-success">
          <i class="fa-sharp fa-light fa-circle-check"></i> Ready
        </span>`;
      case 'processing':
        return html`<span class="badge badge-warning">
          <i class="fa-sharp fa-light fa-spinner-third fa-spin"></i> Processing
        </span>`;
      case 'error':
        return html`<span class="badge badge-error">
          <i class="fa-sharp fa-light fa-circle-exclamation"></i> Error
        </span>`;
      default:
        return html`<span class="badge badge-neutral">${status}</span>`;
    }
  }

  render() {
    const filteredDocs = this._getFilteredDocuments();
    const folders = this._getFolders();

    return html`
      ${iconStyles}
      <!-- Page Header -->
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Knowledge Base</h1>
          <p class="page-description">Upload documents and manage training data for your AI agents</p>
        </div>
        <div class="header-actions">
          <button class="btn btn-primary">
            <i class="fa-sharp fa-light fa-cloud-arrow-up"></i>
            Upload Document
          </button>
          <button class="btn btn-secondary">
            <i class="fa-sharp fa-light fa-link"></i>
            Add URL
          </button>
        </div>
      </div>

      <!-- Stats Row -->
      <div class="stats-row">
        <div class="stat-card">
          <div class="stat-icon documents">
            <i class="fa-sharp-duotone fa-solid fa-files"></i>
          </div>
          <div class="stat-content">
            <span class="metric-value" style="font-size: var(--text-2xl);">${this.documents.length}</span>
            <span class="metric-label">Total Documents</span>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon size">
            <i class="fa-sharp-duotone fa-solid fa-hard-drive"></i>
          </div>
          <div class="stat-content">
            <span class="metric-value" style="font-size: var(--text-2xl);">${this._getTotalSize()}</span>
            <span class="metric-label">Total Size</span>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon agents">
            <i class="fa-sharp-duotone fa-solid fa-robot"></i>
          </div>
          <div class="stat-content">
            <span class="metric-value" style="font-size: var(--text-2xl);">${this._getLinkedAgentsTotal()}</span>
            <span class="metric-label">Linked Agents</span>
          </div>
        </div>
      </div>

      <!-- Two-Column Layout -->
      <div class="content-layout">
        <!-- Sidebar: Folder Tree -->
        <div class="sidebar">
          <div class="sidebar-title">Folders</div>
          <div class="folder-list">
            ${folders.map(folder => html`
              <div
                class="folder-item ${this.selectedFolder === folder.id ? 'active' : ''}"
                @click="${() => this._selectFolder(folder.id)}"
              >
                <i class="${folder.icon}"></i>
                <span class="folder-name">${folder.name}</span>
                <span class="folder-count">${folder.count}</span>
              </div>
            `)}
          </div>
        </div>

        <!-- Main Content -->
        <div class="main-content">
          <!-- Upload Drop Zone -->
          <div class="drop-zone">
            <div class="drop-zone-icon">
              <i class="fa-sharp fa-light fa-cloud-arrow-up"></i>
            </div>
            <div class="drop-zone-text">
              Drag files here or <strong>click to browse</strong>
            </div>
            <div class="drop-zone-hint">
              Supports PDF, DOCX, TXT, CSV -- Max 25 MB per file
            </div>
          </div>

          <!-- Content Toolbar -->
          <div class="content-toolbar">
            <div class="content-toolbar-left">
              <span class="results-count">${filteredDocs.length} document${filteredDocs.length !== 1 ? 's' : ''}</span>
            </div>
            <div class="content-toolbar-right">
              <button
                class="view-toggle-btn ${this.viewMode === 'grid' ? 'active' : ''}"
                @click="${() => this._setViewMode('grid')}"
                title="Grid view"
              >
                <i class="fa-sharp fa-light fa-grid-2"></i>
              </button>
              <button
                class="view-toggle-btn ${this.viewMode === 'list' ? 'active' : ''}"
                @click="${() => this._setViewMode('list')}"
                title="List view"
              >
                <i class="fa-sharp fa-light fa-list"></i>
              </button>
            </div>
          </div>

          <!-- Document Display -->
          ${filteredDocs.length > 0
            ? this.viewMode === 'grid'
              ? this._renderGrid(filteredDocs)
              : this._renderList(filteredDocs)
            : html`
              <div class="empty-state">
                <div class="empty-state-icon">
                  <i class="fa-sharp fa-light fa-folder-open"></i>
                </div>
                <div class="empty-state-title">No documents in this folder</div>
                <div class="empty-state-description">
                  Upload a document or add a URL to get started with this folder.
                </div>
              </div>
            `
          }
        </div>
      </div>
    `;
  }

  _renderGrid(docs) {
    return html`
      <div class="doc-grid">
        ${docs.map(doc => html`
          <div class="doc-card">
            <div class="doc-card-header">
              <div class="doc-type-icon ${doc.type}">
                <i class="${this._getDocTypeIcon(doc.type)}"></i>
              </div>
              <div class="doc-info">
                <div class="doc-name" title="${doc.name}">${doc.name}</div>
                <div class="doc-meta">
                  <span>${doc.size}</span>
                  <span class="doc-meta-sep"></span>
                  <span>${this._formatDate(doc.uploadDate)}</span>
                </div>
              </div>
            </div>

            <div>
              ${this._renderStatusBadge(doc.status)}
            </div>

            <div class="doc-card-footer">
              <div class="doc-footer-left">
                <div class="doc-agents-count">
                  <i class="fa-sharp fa-light fa-robot"></i>
                  ${doc.linkedAgents} agent${doc.linkedAgents !== 1 ? 's' : ''}
                </div>
              </div>
              <div class="doc-actions">
                <button class="doc-action-btn" title="Preview">
                  <i class="fa-sharp fa-light fa-eye"></i>
                </button>
                <button class="doc-action-btn" title="Edit">
                  <i class="fa-sharp fa-light fa-pen-to-square"></i>
                </button>
                <button class="doc-action-btn danger" title="Delete">
                  <i class="fa-sharp fa-light fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        `)}
      </div>
    `;
  }

  _renderList(docs) {
    return html`
      <div class="doc-list">
        ${docs.map(doc => html`
          <div class="doc-list-item">
            <div class="doc-type-icon ${doc.type}" style="width: 36px; height: 36px; font-size: 0.95rem;">
              <i class="${this._getDocTypeIcon(doc.type)}"></i>
            </div>
            <div class="doc-list-name" title="${doc.name}">${doc.name}</div>
            <div class="doc-list-size">${doc.size}</div>
            <div class="doc-list-date">${this._formatDate(doc.uploadDate)}</div>
            <div>${this._renderStatusBadge(doc.status)}</div>
            <div class="doc-list-agents">
              <i class="fa-sharp fa-light fa-robot"></i>
              ${doc.linkedAgents}
            </div>
            <div class="doc-list-actions">
              <button class="doc-action-btn" title="Preview">
                <i class="fa-sharp fa-light fa-eye"></i>
              </button>
              <button class="doc-action-btn" title="Edit">
                <i class="fa-sharp fa-light fa-pen-to-square"></i>
              </button>
              <button class="doc-action-btn danger" title="Delete">
                <i class="fa-sharp fa-light fa-trash"></i>
              </button>
            </div>
          </div>
        `)}
      </div>
    `;
  }
}

customElements.define('knowledge-base', KnowledgeBase);
